/**
 * LockedText plugin for CKEditor 5.
 *
 * Protects text passages marked by the template author as
 * `<span data-locked="true">...</span>` from being edited or deleted
 * by the redakteur, while the rest of the text block stays fully editable.
 *
 * `data-locked` is only ever written into the template HTML up front,
 * never toggled at runtime, so this plugin does not register a toolbar
 * button or a command - it only recognizes and protects the attribute
 * on load and keeps protecting it as the surrounding text is edited.
 *
 * Raw `contenteditable="false"` on the span is deliberately not used
 * (cross-browser Backspace/selection bugs). Protection instead works by
 * cancelling the relevant commands' `execute` event whenever the current
 * selection overlaps, or is directly adjacent to, locked text - see
 * https://ckeditor.com/docs/ckeditor5/latest/framework/tutorials/widgets/implementing-an-inline-widget.html
 * and the (inverse) approach used by @ckeditor/ckeditor5-restricted-editing
 * for the general pattern of guarding commands via their `execute` event.
 */
import { Plugin } from 'ckeditor5';
import './lockedtext.css';

const LOCK_ATTRIBUTE = 'locked';

// AttributeCommand-based formatting commands that must never be applied
// across locked text, in addition to typing (insertText) and deleting
// (delete/deleteForward), which are guarded separately below.
const GUARDED_FORMATTING_COMMANDS = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'subscript',
  'superscript',
  'removeFormat',
  'link',
  'unlink',
  'fontSize',
  'fontFamily',
  'fontColor',
  'fontBackgroundColor',
];

export default class LockedText extends Plugin {
  init() {
    const editor = this.editor;
    this._lastSafePosition = null;
    this._isCorrectingSelection = false;

    this._defineSchema(editor);
    this._defineConverters(editor);
    this._guardCaretPosition(editor);
    this._guardTyping(editor);
    this._guardDeletion(editor);
    this._guardFormatting(editor);
    this._guardInsertContent(editor);
  }

  _defineSchema(editor) {
    editor.model.schema.extend('$text', {
      allowAttributes: LOCK_ATTRIBUTE,
    });
  }

  _defineConverters(editor) {
    // `attributeToAttribute` only adds an attribute onto a model range that some
    // other (element) converter already produced for the very same view element -
    // it does not create that range itself (see the "shallow" upcast attribute
    // converter in @ckeditor/ckeditor5-engine). A `data-locked` span nested inside
    // another attribute span (e.g. FontSize's `<span class="text-default">`) would
    // therefore silently upcast to nothing. `elementToAttribute` (used by the core
    // FontSize/Bold/etc. converters themselves) converts its own children first,
    // so it works regardless of nesting.
    editor.conversion.for('upcast').elementToAttribute({
      view: {
        name: 'span',
        attributes: {
          'data-locked': 'true',
        },
      },
      model: {
        key: LOCK_ATTRIBUTE,
        value: true,
      },
    });

    editor.conversion.for('downcast').attributeToElement({
      model: LOCK_ATTRIBUTE,
      view: (attributeValue, { writer }) => {
        if (!attributeValue) {
          return;
        }
        // Higher priority than FontSize's span (priority 7) so a locked span nested
        // inside e.g. a fontSize span downcasts back as the innermost element again.
        return writer.createAttributeElement('span', { 'data-locked': 'true' }, { priority: 10 });
      },
    });
  }

  /**
   * The caret itself must never come to rest inside locked text - only a
   * (non-collapsed) selection is allowed to cover it, for copying. Whenever
   * a collapsed selection lands inside a locked run, it is bounced back out
   * to a boundary of that run: in the direction of travel for a single-step
   * move (arrow keys - so the whole run is skipped over in one step), or to
   * the nearer boundary for a "teleport" (e.g. a mouse click).
   */
  _guardCaretPosition(editor) {
    const model = editor.model;

    this.listenTo(model.document.selection, 'change:range', () => {
      if (this._isCorrectingSelection) {
        return;
      }

      const selection = model.document.selection;
      if (!selection.isCollapsed) {
        this._lastSafePosition = null;
        return;
      }

      const position = selection.getFirstPosition();
      const textNode = position.textNode;

      if (!isLockedNode(textNode)) {
        this._lastSafePosition = position;
        return;
      }

      const before = model.createPositionBefore(textNode);
      const after = model.createPositionAfter(textNode);
      const lastSafePosition = this._lastSafePosition;
      const isSingleStep =
        lastSafePosition &&
        lastSafePosition.root === position.root &&
        Math.abs(lastSafePosition.offset - position.offset) === 1;

      const target = isSingleStep
        ? (position.offset > lastSafePosition.offset ? after : before)
        : (position.offset - before.offset <= after.offset - position.offset ? before : after);

      this._isCorrectingSelection = true;
      model.change(writer => writer.setSelection(target));
      this._isCorrectingSelection = false;
      this._lastSafePosition = target;
    });
  }

  /**
   * Typing (insertText/input) is cancelled whenever the insertion point
   * overlaps or touches locked text - touching is blocked too, otherwise
   * newly typed characters would inherit the "locked" attribute from the
   * adjacent character (the usual "typing continues formatting" behavior).
   */
  _guardTyping(editor) {
    const insertTextCommand = editor.commands.get('insertText');
    if (!insertTextCommand) {
      return;
    }

    this.listenTo(insertTextCommand, 'execute', (evt, args) => {
      const [options = {}] = args;
      const range =
        options.range ||
        (options.selection && options.selection.getFirstRange()) ||
        editor.model.document.selection.getFirstRange();

      if (range && touchesLocked(range)) {
        evt.stop();
      }
    }, { priority: 'highest' });
  }

  /**
   * Backspace/Delete: a collapsed caret is blocked when the character it
   * would consume is locked. A non-collapsed selection that only partially
   * overlaps locked text still deletes the free part of the selection and
   * keeps the locked part intact, instead of cancelling the whole action.
   */
  _guardDeletion(editor) {
    [
      { name: 'delete', direction: 'backward' },
      { name: 'deleteForward', direction: 'forward' },
    ].forEach(({ name, direction }) => {
      const command = editor.commands.get(name);
      if (!command) {
        return;
      }

      this.listenTo(command, 'execute', evt => {
        const selection = editor.model.document.selection;

        if (selection.isCollapsed) {
          const position = selection.focus;
          const adjacentNode = direction === 'backward' ? position.nodeBefore : position.nodeAfter;
          if (isLockedNode(adjacentNode)) {
            evt.stop();
          }
          return;
        }

        const range = selection.getFirstRange();
        if (!rangeHasLocked(range)) {
          return;
        }

        evt.stop();
        deleteOnlyFreeParts(editor, range);
      }, { priority: 'highest' });
    });
  }

  /**
   * Formatting commands (bold, italic, ...) are cancelled outright - unlike
   * deletion there is no "partial" carve-out here: a selection touching
   * locked text at all leaves the formatting command a no-op.
   */
  _guardFormatting(editor) {
    GUARDED_FORMATTING_COMMANDS.forEach(commandName => {
      const command = editor.commands.get(commandName);
      if (!command) {
        return;
      }

      this.listenTo(command, 'execute', evt => {
        const touched = Array.from(editor.model.document.selection.getRanges()).some(touchesLocked);
        if (touched) {
          evt.stop();
        }
      }, { priority: 'highest' });
    });
  }

  /**
   * Covers paste and drag&drop, both of which insert content via
   * `model.insertContent()` rather than through the guarded commands above.
   */
  _guardInsertContent(editor) {
    const model = editor.model;

    this.listenTo(model, 'insertContent', (evt, args) => {
      const [, selectable] = args;
      const selection = !selectable
        ? model.document.selection
        : selectable.is && selectable.is('selection')
          ? selectable
          : model.createSelection(selectable);

      const range = selection.getFirstRange && selection.getFirstRange();
      if (range && touchesLocked(range)) {
        evt.stop();
      }
    }, { priority: 'high' });
  }
}

function isLockedNode(node) {
  return !!node && typeof node.getAttribute === 'function' && !!node.getAttribute(LOCK_ATTRIBUTE);
}

function rangeHasLocked(range) {
  for (const item of range.getItems()) {
    if (isLockedNode(item)) {
      return true;
    }
  }
  return false;
}

function touchesLocked(range) {
  if (rangeHasLocked(range)) {
    return true;
  }

  return (
    isLockedNode(range.start.textNode) ||
    isLockedNode(range.start.nodeBefore) ||
    isLockedNode(range.start.nodeAfter) ||
    isLockedNode(range.end.textNode) ||
    isLockedNode(range.end.nodeBefore) ||
    isLockedNode(range.end.nodeAfter)
  );
}

/**
 * Splits `range` into the sub-ranges that are NOT locked and removes only
 * those, so a selection spanning free-locked-free (or similar) only loses
 * its free parts.
 */
function deleteOnlyFreeParts(editor, range) {
  const model = editor.model;
  const subranges = getFreeSubranges(model, range);

  if (subranges.length === 0) {
    return;
  }

  const caretPosition = subranges[0].start;

  model.change(writer => {
    for (let i = subranges.length - 1; i >= 0; i--) {
      model.deleteContent(model.createSelection(subranges[i]));
    }
    writer.setSelection(caretPosition);
  });
}

function getFreeSubranges(model, range) {
  const subranges = [];
  let segmentStart = range.start;

  for (const { type, item, previousPosition, nextPosition } of range.getWalker({ singleCharacters: false })) {
    if (type !== 'text' || !isLockedNode(item)) {
      continue;
    }

    if (!segmentStart.isEqual(previousPosition)) {
      subranges.push(model.createRange(segmentStart, previousPosition));
    }
    segmentStart = nextPosition;
  }

  if (!segmentStart.isEqual(range.end)) {
    subranges.push(model.createRange(segmentStart, range.end));
  }

  return subranges;
}
